import Image from 'next/image';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import { useAppContext } from '../../hooks/use-app-context';
import { AreaConfigMap, ExternalLink } from '../../types/area';
import { Room } from '../../types/room';
import { AlertPopup } from '../alert-popup';
import './room-list.css'; // スクロールバースタイル用のCSSをインポート

/**
 * ルームリストコンポーネントのプロパティ
 */
export interface RoomListProps {
  /** 表示状態 */
  visible: boolean;
  /** 閉じるボタンがクリックされたときのコールバック */
  onClose: () => void;
  /** ホストモードかどうか */
  isHost?: boolean;
  /** 同期が有効かどうか */
  isSyncEnabled?: boolean;
  /** ツアーモードかどうか */
  isTourMode?: boolean;
}

/**
 * ルームリストコンポーネント
 *
 * ヘッダーの下に表示されるサムネイル付きのルームリスト
 */
export function RoomList({
  visible,
  onClose,
  isHost = false,
  isSyncEnabled = false,
  isTourMode = false
}: RoomListProps) {
  // アプリコンテキストからルーム情報を取得
  const { state, switchRoom, dispatch } = useAppContext();
  const { rooms, currentRoomId } = state;

  // アラートポップアップの状態
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  // エリア設定の状態
  const [currentAreaLinks, setCurrentAreaLinks] = useState<ExternalLink[]>([]);

  // ルームリストの表示状態が変わったときにアプリケーションの状態を更新
  useEffect(() => {
    dispatch({ type: 'SET_ROOM_LIST_VISIBLE', payload: visible });
  }, [visible, dispatch]);

  // エリア設定を読み込み
  useEffect(() => {
    fetch('/data/area-config.json')
      .then((res) => res.json())
      .then((data: AreaConfigMap) => {
        // 現在のエリアの外部リンクを取得
        const params = new URLSearchParams(window.location.search);
        const areaParam = params.get('area');

        if (areaParam && data[areaParam]?.externalLinks) {
          setCurrentAreaLinks(data[areaParam].externalLinks);
        } else {
          setCurrentAreaLinks([]);
        }
      })
      .catch((err) => {
        console.error('Failed to load area config:', err);
        setCurrentAreaLinks([]);
      });
  }, []);

  // 固定スタイル設定
  const containerPadding = '0px';
  // サムネイルのサイズを固定
  const thumbnailWidth = 183; // px
  const thumbnailHeight = 130; // px
  // ギャップ設定
  const gap = '15px';

  // ドラッグスクロール用の状態とref
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // マウスイベントハンドラー
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);

    // クリックイベントが伝播しないようにする
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5; // スクロール速度の調整
      containerRef.current.scrollLeft = scrollLeft - walk;

      // クリックイベントが伝播しないようにする
      e.preventDefault();
      e.stopPropagation();
    },
    [isDragging, startX, scrollLeft]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // タッチイベントハンドラー
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!containerRef.current || e.touches.length === 0) return;

    const currentRef = containerRef.current;
    // 型アサーションを使用して、touchesが存在することを保証
    const touch = e.touches[0] as Touch;

    setIsDragging(true);
    setStartX(touch.pageX - currentRef.offsetLeft);
    setScrollLeft(currentRef.scrollLeft);
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !containerRef.current || e.touches.length === 0) return;

      const currentRef = containerRef.current;
      // 型アサーションを使用して、touchesが存在することを保証
      const touch = e.touches[0] as Touch;

      const x = touch.pageX - currentRef.offsetLeft;
      const walk = (x - startX) * 1.5; // スクロール速度の調整
      currentRef.scrollLeft = scrollLeft - walk;

      // passive: falseのイベントリスナーで使用するため、ここでは問題ない
      e.preventDefault();
      e.stopPropagation();
    },
    [isDragging, startX, scrollLeft]
  );

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // イベントリスナーの設定と解除
  useEffect(() => {
    const handleMouseMoveEvent = (e: MouseEvent) => handleMouseMove(e);
    const handleMouseUpEvent = () => handleMouseUp();
    const handleTouchMoveEvent = (e: TouchEvent) => handleTouchMove(e);
    const handleTouchEndEvent = () => handleTouchEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMoveEvent);
      document.addEventListener('mouseup', handleMouseUpEvent);
      document.addEventListener('touchmove', handleTouchMoveEvent, { passive: false });
      document.addEventListener('touchend', handleTouchEndEvent);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveEvent);
      document.removeEventListener('mouseup', handleMouseUpEvent);
      document.removeEventListener('touchmove', handleTouchMoveEvent);
      document.removeEventListener('touchend', handleTouchEndEvent);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  // キーボード操作のハンドラー
  const handleKeyDown = (e: React.KeyboardEvent, roomId: string) => {
    // EnterキーまたはSpaceキーが押された場合にのみルーム選択を実行
    if (e.key === 'Enter' || e.key === ' ') {
      handleRoomClick(roomId);
    }
  };

  // ルームがクリックされたときのハンドラー
  const handleRoomClick = (roomId: string) => {
    // ドラッグ中のクリックは無視する
    if (isDragging) return;

    // ゲストモードで同期有効時はルーム遷移を無効化し、アラートポップアップを表示
    if (!isHost && isSyncEnabled) {
      console.log('[ROOM_LIST] Room click ignored: Guest mode with sync enabled');
      setIsAlertVisible(true);
      return;
    }

    switchRoom(roomId);
    onClose();
  };

  // アラートポップアップが閉じられたときのハンドラー
  const handleAlertClose = () => {
    setIsAlertVisible(false);
  };

  // 表示されていない場合は何も表示しない
  if (!visible) {
    return null;
  }

  // スタイル定義
  const containerStyle = {
    position: 'absolute' as const,
    top: '78px',
    left: 0,
    width: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    padding: `0 ${containerPadding} ${containerPadding} ${containerPadding}`,
    zIndex: 999,
    height: '150px',
    boxSizing: 'border-box' as const
  };

  return (
    <div style={containerStyle}>
      <div
        ref={containerRef}
        className={`room-list-container ${isDragging ? 'dragging' : ''}`}
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap,
          maxHeight: '150px',
          overflowX: 'auto',
          overflowY: 'hidden',
          marginLeft: '0px', // スクロールエリアはすべて
          paddingLeft: isTourMode ? '321px' : '88px', // ツアーモードの時だけ内部パディングを追加
          paddingRight: '24px', // 一番右の要素の右側に余白を設ける
          paddingBottom: '10px', // スクロールバー用の余白
          width: 'calc(100% - 0px)',
          whiteSpace: 'nowrap', // 要素が折り返されないようにする
          boxSizing: 'border-box'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {rooms.map((room: Room) => {
          // 各ルームの最新の履歴を取得（サムネイル用）
          const latestHistory = room.histories.length > 0 ? room.histories[room.histories.length - 1] : null;
          const thumbnailUrl = latestHistory?.thumbnailUrl || '/room-placeholder.png';
          const isCurrentRoom = room.id === currentRoomId;

          return (
            <div
              key={room.id}
              aria-label={`${room.name}を選択`}
              className={`room-thumbnail-item ${!isHost && isSyncEnabled ? 'cursor-not-allowed' : ''}`}
              role='button'
              tabIndex={0}
              title={room.name}
              style={{
                width: `${thumbnailWidth}px`,
                height: `${thumbnailHeight}px`,
                cursor: !isHost && isSyncEnabled ? 'not-allowed' : 'pointer', // 互換性のために残す
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                flexShrink: 0 // サイズが縮小されないようにする
              }}
              onClick={() => handleRoomClick(room.id)}
              onKeyDown={(e) => handleKeyDown(e, room.id)}
              onMouseOut={(e) => {
                const imageWrapper = e.currentTarget.querySelector('.room-image-wrapper') as HTMLElement;
                if (imageWrapper) {
                  imageWrapper.style.transform = 'scale(1)';
                }
              }}
              onMouseOver={(e) => {
                const imageWrapper = e.currentTarget.querySelector('.room-image-wrapper') as HTMLElement;
                if (imageWrapper) {
                  imageWrapper.style.transform = 'scale(1.15)';
                }
              }}
            >
              {isCurrentRoom && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    border: '5px solid #22A9BC',
                    borderRadius: '16px',
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                />
              )}
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '16px'
                }}
              >
                <div
                  className='room-image-wrapper'
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transition: 'transform 0.3s ease',
                    transform: 'scale(1)'
                  }}
                >
                  <Image
                    alt={room.name}
                    height={thumbnailHeight}
                    src={thumbnailUrl}
                    width={thumbnailWidth}
                    style={{
                      width: `${thumbnailWidth}px`,
                      height: `${thumbnailHeight}px`,
                      objectFit: 'cover',
                      borderRadius: '6px'
                    }}
                  />
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    borderRadius: '16px',
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              </div>
              <div
                className='room-name-overlay'
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '24px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6)',
                  pointerEvents: 'none'
                }}
              >
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '2.5em',
                    lineHeight: '1.25',
                    width: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    margin: '0 auto' // 中央揃え
                  }}
                >
                  {room.name}
                </div>
              </div>
            </div>
          );
        })}

        {/* 区切り線（ルーム一覧と外部リンクの間） */}
        {currentAreaLinks.length > 0 && (
          <div
            style={{
              width: '2px',
              height: `${thumbnailHeight}px`,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              flexShrink: 0,
              margin: '0 8px'
            }}
          />
        )}

        {/* エリアレベルの外部リンクボタン（ルーム一覧の右端に表示） */}
        {currentAreaLinks.map((link, index) => (
          <a
            key={`area-link-${index}`}
            className='external-link-button'
            href={link.url}
            rel='noopener noreferrer'
            target='_blank'
            style={{
              width: `${thumbnailWidth}px`,
              height: `${thumbnailHeight}px`,
              cursor: 'pointer',
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
              flexShrink: 0,
              textDecoration: 'none'
            }}
            onMouseOut={(e) => {
              const imageWrapper = e.currentTarget.querySelector('.external-link-image-wrapper') as HTMLElement;
              if (imageWrapper) {
                imageWrapper.style.transform = 'scale(1)';
              }
            }}
            onMouseOver={(e) => {
              const imageWrapper = e.currentTarget.querySelector('.external-link-image-wrapper') as HTMLElement;
              if (imageWrapper) {
                imageWrapper.style.transform = 'scale(1.15)';
              }
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '16px'
              }}
            >
              <div
                className='external-link-image-wrapper'
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  transform: 'scale(1)'
                }}
              >
                {link.imageUrl ? (
                  <>
                    <div
                      style={{
                        width: `${thumbnailWidth}px`,
                        height: `${thumbnailHeight}px`,
                        backgroundImage: `url(${link.imageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '6px'
                      }}
                    />
                    {link.color && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundColor: link.color,
                          opacity: 0.3,
                          borderRadius: '6px',
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      width: `${thumbnailWidth}px`,
                      height: `${thumbnailHeight}px`,
                      backgroundColor: link.backgroundColor || '#22A9BC',
                      borderRadius: '6px'
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '16px',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
            </div>
            <div
              className='external-link-name-overlay'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '24px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6)',
                pointerEvents: 'none'
              }}
            >
              <div
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxHeight: '2.5em',
                  lineHeight: '1.25',
                  width: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  margin: '0 auto'
                }}
              >
                {link.label}
              </div>
            </div>

            {/* 外部リンクアイコン */}
            <div
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                fontSize: '14px',
                zIndex: 2,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
              }}
            >
              🔗
            </div>
          </a>
        ))}
      </div>

      {/* アラートポップアップ */}
      <AlertPopup
        message='画面左上の「ホスト操作に追従」をオフにすると選択できます'
        visible={isAlertVisible}
        onClose={handleAlertClose}
      />
    </div>
  );
}
